import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { getRuleSummary } from '../displayStrings';
import { messages } from '../messages';
import { useStyles } from '../styles';
import { AIRule } from '../types';

interface RuleActionFilterProps {
  rules: AIRule[];
  value: string;
  onChange: (ruleId: string) => void;
}

/** Longest rule description kept on an option's first line. */
const MAX_TITLE_LENGTH = 72;

/** Keeps the option list readable when the filter itself is narrow. */
const POPOVER_MIN_WIDTH = 480;

/**
 * Rules have no short name, so the first line is the rule's description
 * flattened onto one line and clipped: long enough to tell two rules on the
 * same trigger apart, short enough that the closed Select still reads as a
 * label rather than a paragraph.
 */
export const getRuleActionFilterTitle = (rule: AIRule): string => {
  const description = (rule.human_description || rule.condition_description)
    ?.replace(/\s+/g, ' ')
    .trim();
  if (!description) {
    return getRuleSummary(rule) || rule.id;
  }
  return description.length > MAX_TITLE_LENGTH
    ? `${description.slice(0, MAX_TITLE_LENGTH).trimEnd()}…`
    : description;
};

/**
 * Second line: the structural "trigger → action" summary plus the leading
 * segment of the rule id, which is what disambiguates rules whose descriptions
 * clip to the same text.
 */
export const getRuleActionFilterSubtitle = (rule: AIRule): string =>
  [getRuleSummary(rule), rule.id.split('-')[0]].filter(Boolean).join(' · ');

const RuleActionFilter: React.FC<RuleActionFilterProps> = ({
  rules,
  value,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const sortedRules = useMemo(
    () =>
      rules
        .slice()
        .sort((a, b) =>
          getRuleActionFilterTitle(a).localeCompare(
            getRuleActionFilterTitle(b),
          ),
        ),
    [rules],
  );

  return (
    <Select
      fullWidth
      label={formatMessage(messages.ruleActionFilterLabel)}
      value={value}
      onChange={onChange}
      popoverStyle={{ minWidth: POPOVER_MIN_WIDTH, maxWidth: '90vw' }}
    >
      <SelectOption
        value=""
        title={formatMessage(messages.ruleActionFilterAll)}
      />
      {sortedRules.map(rule => (
        <SelectOption
          key={rule.id}
          value={rule.id}
          className={styles.ruleActionFilterOption}
          title={getRuleActionFilterTitle(rule)}
          subtitle={getRuleActionFilterSubtitle(rule)}
        />
      ))}
    </Select>
  );
};

export default RuleActionFilter;
